// Users database operations
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

export const usersDB = {
  // Create user profile on signup
  createUser: async (userId, { name, email, schoolId, currentBelt, dailyGoalMinutes }) => {
    try {
      const userRef = doc(db, 'users', userId);
      await setDoc(userRef, {
        profile: {
          name,
          email,
          schoolId: schoolId || 'kung-fu-southern-praying-mantis',
          currentBelt: currentBelt || 'white-belt',
          track: 'adult',
          language: 'he',  // Default
          avatarUrl: null
        },
        settings: {
          dailyGoalMinutes: dailyGoalMinutes || 10,
          reminderTime: '18:00',
          reminderEnabled: false  // User must opt-in
        },
        stats: {
          currentStreak: 0,
          longestStreak: 0,
          totalTrainingSeconds: 0,
          totalSessions: 0,
          lastTrainingDate: null,
          createdAt: serverTimestamp(),
          // Shuriken (currency) fields
          shurikens: {
            total: 0,
            weekly: 0,
            multiplier: 1.0,
          },
          currentLeague: 'lead',
          lastKnownRank: null,
          hasCompletedFirstDrill: false,
        }
      });
      return userRef;
    } catch (error) {
      console.error('Error creating user:', error);
      throw error;
    }
  },

  // Get user data
  getUserData: async (userId) => {
    try {
      const docRef = doc(db, 'users', userId);
      const docSnap = await getDoc(docRef);
      return docSnap.exists() ? docSnap.data() : null;
    } catch (error) {
      console.error('Error getting user data:', error);
      throw error;
    }
  },

  // Update profile
  updateProfile: async (userId, profileData) => {
    try {
      const userRef = doc(db, 'users', userId);
      const updates = {};
      
      if (profileData.name !== undefined) {
        updates['profile.name'] = profileData.name;
      }
      if (profileData.currentBelt !== undefined) {
        updates['profile.currentBelt'] = profileData.currentBelt;
      }
      if (profileData.track !== undefined) {
        updates['profile.track'] = profileData.track;
      }
      if (profileData.language !== undefined) {
        updates['profile.language'] = profileData.language;
      }
      if (profileData.avatarUrl !== undefined) {
        updates['profile.avatarUrl'] = profileData.avatarUrl;
      }
      
      await updateDoc(userRef, updates);
    } catch (error) {
      console.error('Error updating profile:', error);
      throw error;
    }
  },

  // Update settings
  updateSettings: async (userId, settingsData) => {
    try {
      const userRef = doc(db, 'users', userId);
      const updates = {};
      
      if (settingsData.dailyGoalMinutes !== undefined) {
        updates['settings.dailyGoalMinutes'] = settingsData.dailyGoalMinutes;
      }
      if (settingsData.reminderTime !== undefined) {
        updates['settings.reminderTime'] = settingsData.reminderTime;
      }
      if (settingsData.reminderEnabled !== undefined) {
        updates['settings.reminderEnabled'] = settingsData.reminderEnabled;
      }
      
      await updateDoc(userRef, updates);
    } catch (error) {
      console.error('Error updating settings:', error);
      throw error;
    }
  },

  // Save push subscription for web push notifications
  savePushSubscription: async (userId, subscription) => {
    try {
      // Write directly to Firestore (no API call needed)
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        pushSubscription: subscription ? subscription.toJSON() : null,
        pushSubscriptionUpdatedAt: serverTimestamp()
      });
      
      console.log('Push subscription saved to Firestore');
      return { success: true };
    } catch (error) {
      console.error('Error saving push subscription:', error);
      throw error;
    }
  },

  // Internal: Update stats (called by trainings.logTraining)
  _updateStats: async (userId, statsUpdate) => {
    try {
      const userRef = doc(db, 'users', userId);
      const updates = {};
      
      Object.keys(statsUpdate).forEach(key => {
        updates[`stats.${key}`] = statsUpdate[key];
      });
      
      await updateDoc(userRef, updates);
    } catch (error) {
      console.error('Error updating stats:', error);
      throw error;
    }
  }
};

// Legacy exports for backward compatibility
export const getUser = usersDB.getUserData;
export const createUser = usersDB.createUser;
export const updateUser = usersDB.updateProfile;
export const updateUserStats = usersDB._updateStats;
